import React from 'react';
import { Calendar, Clock, Tag, Check } from 'lucide-react';
import ThemedCard from './themed/ThemedCard';
import { Checkbox } from './ui/checkbox';
import { TimelineEvent } from '../types/invitation';

interface TimelineTaskCardProps {
  event: TimelineEvent;
  onToggleComplete: (eventId: string, isCompleted: boolean) => void;
  onEdit: (event: TimelineEvent) => void;
}

const TimelineTaskCard: React.FC<TimelineTaskCardProps> = ({
  event,
  onToggleComplete,
  onEdit
}) => {
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short'
      });
    } catch {
      return dateString;
    }
  };

  const getDaysUntilTarget = (targetDate: string) => {
    try {
      const target = new Date(targetDate);
      const now = new Date();
      const diffTime = target.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return `已過期 ${Math.abs(diffDays)} 天`;
      } else if (diffDays === 0) {
        return '今天';
      } else if (diffDays === 1) {
        return '明天';
      } else {
        return `還有 ${diffDays} 天`;
      }
    } catch {
      return '';
    }
  };

  const getStatusColor = (targetDate: string, isCompleted: boolean) => {
    if (isCompleted) {
      return 'text-green-600';
    }
    
    try {
      const target = new Date(targetDate);
      const now = new Date();
      const diffTime = target.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return 'text-red-500'; // 已過期
      } else if (diffDays <= 3) {
        return 'text-orange-500'; // 即將到期
      } else {
        return 'text-wedding-text-body'; // 正常
      }
    } catch {
      return 'text-wedding-text-body';
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    onToggleComplete(event.id, checked);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // 避免點擊 checkbox 時觸發卡片點擊事件
    if ((e.target as HTMLElement).closest('[data-checkbox]')) {
      return;
    }
    onEdit(event);
  };

  return (
    <ThemedCard className="cursor-pointer hover:shadow-md transition-shadow duration-200">
      <div className="p-4" onClick={handleCardClick}>
        <div className="flex items-start gap-3">
          {/* 完成狀態 Checkbox */}
          <div data-checkbox className="mt-1">
            <Checkbox
              checked={event.is_completed}
              onCheckedChange={handleCheckboxChange}
              className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
            />
          </div>

          {/* 任務內容 */}
          <div className="flex-1 min-w-0">
            {/* 任務標題 */}
            <h3 className={`text-lg font-semibold text-wedding-text-title mb-1 ${
              event.is_completed ? 'line-through text-gray-500' : ''
            }`}>
              {event.title}
            </h3>

            {/* 目標日期和倒數 */}
            <div className={`flex items-center gap-2 text-sm mb-2 ${
              getStatusColor(event.target_date, event.is_completed)
            }`}>
              <Calendar className="w-4 h-4" />
              <span>{formatDate(event.target_date)}</span>
              <Clock className="w-4 h-4 ml-2" />
              <span className="font-medium">
                {getDaysUntilTarget(event.target_date)}
              </span>
            </div>

            {/* 任務描述 */}
            {event.description && (
              <p className={`text-sm text-wedding-text-body mb-2 line-clamp-2 ${
                event.is_completed ? 'text-gray-400' : ''
              }`}>
                {event.description}
              </p>
            )}

            {/* 分類標籤 */}
            {event.category && (
              <div className="flex items-center gap-1 text-xs">
                <Tag className="w-3 h-3" />
                <span className={`px-2 py-1 rounded-full ${
                  event.is_completed 
                    ? 'bg-gray-100 text-gray-400' 
                    : 'bg-wedding-pink/10 text-wedding-pink'
                }`}>
                  {event.category}
                </span>
              </div>
            )}

            {/* 完成狀態指示器 */}
            {event.is_completed && (
              <div className="flex items-center gap-1 text-green-600 text-xs mt-2">
                <Check className="w-3 h-3" />
                <span>已完成</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ThemedCard>
  );
};

export default TimelineTaskCard;